import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  InsightsEmptyState,
  InsightsErrorState,
  InsightsTableSkeleton,
} from "../../components/Insights/InsightsAsyncState";
import BreakdownDialogTitle from "./BreakdownDialogTitle";
import ProductImageViewer from "./ProductImageViewer";
import RevisionDeviationDetails from "./RevisionDeviationDetails";
import {
  formatPriceHistoryBreakdownAggregateContext,
  mapPriceHistoryBreakdownRowsToDisplayItems,
} from "./priceHistoryBreakdownDisplay";

const breakdownTableActionButtonProps = {
  size: "small",
  variant: "outlined",
  color: "secondary",
};

const hideOnMobileCellSx = { display: { xs: "none", md: "table-cell" } };

function PriceHistoryBreakdownDialog({ open, aggregateRow, rows, isLoading, error, onClose, onEditRecord }) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedImageRowKey, setExpandedImageRowKey] = useState("");

  useEffect(() => {
    if (open) {
      setPage(0);
      setExpandedImageRowKey("");
    }
  }, [open, aggregateRow]);

  const items = useMemo(() => mapPriceHistoryBreakdownRowsToDisplayItems(rows), [rows]);

  const aggregateContextLine = useMemo(
    () => formatPriceHistoryBreakdownAggregateContext(aggregateRow),
    [aggregateRow]
  );

  const pagedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  }, [items, page, rowsPerPage]);

  const toggleInlineImage = useCallback((rowId, imageIndex) => {
    const nextKey = `${rowId}:${imageIndex}`;
    setExpandedImageRowKey((prev) => (prev === nextKey ? "" : nextKey));
  }, []);

  const actionButtonGroupSx = isMobileViewport
    ? { flexDirection: "column", alignItems: "stretch", "& .MuiButton-root": { justifyContent: "flex-start" } }
    : undefined;

  return (
    <Dialog
      className="insights-dialog"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobileViewport}
    >
      <BreakdownDialogTitle title="Pricing history breakdown" onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={2}>
          {aggregateContextLine ? (
            <Typography variant="body2" color="text.secondary">
              {aggregateContextLine}
            </Typography>
          ) : null}

          {error ? (
            <InsightsErrorState
              message={error}
              fallbackMessage="Something went wrong while loading breakdown rows."
            />
          ) : null}

          {isLoading ? (
            <InsightsTableSkeleton />
          ) : (
            <>
              <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <Table
                  sx={{
                    width: "100%",
                    minWidth: 720,
                    border: "1px solid #eaeaea",
                    backgroundColor: "#fff",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Retailer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Province</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit price</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 600, ...hideOnMobileCellSx }}>Weight</TableCell>
                      <TableCell sx={{ fontWeight: 600, ...hideOnMobileCellSx }}>Human verified</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9}>
                          <InsightsEmptyState message="No rows found for this group." />
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedItems.map((item) => (
                        <Fragment key={item.id}>
                          <TableRow>
                            <TableCell>{item.recordAt}</TableCell>
                            <TableCell>{item.product}</TableCell>
                            <TableCell>{item.retailer}</TableCell>
                            <TableCell>{item.province}</TableCell>
                            <TableCell>{item.unitPrice}</TableCell>
                            <TableCell>{item.price}</TableCell>
                            <TableCell sx={hideOnMobileCellSx}>{item.weight}</TableCell>
                            <TableCell sx={hideOnMobileCellSx}>
                              {item.verified ? (
                                <Chip size="small" color="success" label="Verified" />
                              ) : (
                                <Chip size="small" variant="outlined" label="Not verified" />
                              )}
                            </TableCell>
                            <TableCell>
                              <ButtonGroup
                                {...breakdownTableActionButtonProps}
                                sx={actionButtonGroupSx}
                                aria-label={`Actions for ${item.product}`}
                              >
                                {item.images.map((url, imageIndex) => {
                                  const imageKey = `${item.id}:${imageIndex}`;
                                  const isExpanded = expandedImageRowKey === imageKey;
                                  return (
                                    <Button
                                      key={imageKey}
                                      startIcon={<PreviewOutlinedIcon />}
                                      aria-pressed={isExpanded}
                                      aria-label={`Preview image ${imageIndex + 1}`}
                                      onClick={() => toggleInlineImage(item.id, imageIndex)}
                                    >
                                      Preview
                                    </Button>
                                  );
                                })}
                                <Button
                                  startIcon={<VisibilityOutlinedIcon />}
                                  onClick={() => onEditRecord?.(item)}
                                >
                                  View
                                </Button>
                              </ButtonGroup>
                            </TableCell>
                          </TableRow>
                          {item.images.map((url, imageIndex) => {
                            const imageKey = `${item.id}:${imageIndex}`;
                            if (expandedImageRowKey !== imageKey) return null;
                            return (
                              <TableRow key={`${imageKey}:preview`}>
                                <TableCell colSpan={9} sx={{ backgroundColor: "#fafafa" }}>
                                  <Stack spacing={1.5}>
                                    <ProductImageViewer
                                      src={url}
                                      alt={`${item.product} image ${imageIndex + 1}`}
                                      minHeight={280}
                                      maxHeight={320}
                                    />
                                    <RevisionDeviationDetails
                                      revisionStatus={item.revisionStatus}
                                      revisionMetadata={item.revisionMetadata}
                                      unitPriceValue={item.unitPriceValue}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={items.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, nextPage) => {
                  setExpandedImageRowKey("");
                  setPage(nextPage);
                }}
                onRowsPerPageChange={(event) => {
                  setExpandedImageRowKey("");
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default PriceHistoryBreakdownDialog;
